import { RRule } from 'rrule';

import dateService from '@gem/services/date-service';
import { isRussianLocale } from '@gem/utils';

import { getLanguage } from 'APP/utils/getLanguage';

import { pluralize } from './pluralize';

const getDayNames = (dayIndices: number[]): string => {
  if (dayIndices.length === 0) return '';

  const sortedIndices = [...dayIndices].sort((a, b) => a - b);
  const baseDate = new Date(2026, 1, 2); // Понедельник
  const formatter = new Intl.DateTimeFormat(getLanguage(), { weekday: 'long' });

  const processedDays = sortedIndices.map((index, i) => {
    const date = new Date(baseDate);
    date.setDate(baseDate.getDate() + index);
    const dayName = formatter.format(date).toLowerCase();

    let formattedDay = dayName.endsWith('а') ? dayName.replace(/а$/, 'у') : dayName;

    if (i === 0) {
      const preposition = dayName === 'вторник' ? 'во' : 'в';
      formattedDay = `${preposition} ${formattedDay}`;
    }

    return formattedDay.charAt(0).toUpperCase() + formattedDay.slice(1);
  });

  return processedDays.join(', ');
};

const getUntilDateStr = (untilDate: Date | null): string => {
  return untilDate
    ? `до ${dateService.format(untilDate, {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })}`
    : '';
};

const getReccurencyCountStr = (count: number | null): string => {
  return count
    ? `${count} ${pluralize(count, {
        one: 'раз',
        few: 'раза',
        many: 'раз',
        zero: 'раз',
        two: 'раза',
        other: 'раз',
      })}`
    : '';
};

export const getUnitLabel = (value: number, unit: string): string => {
  const formatter = new Intl.NumberFormat(getLanguage(), {
    style: 'unit',
    unit: unit,
    unitDisplay: 'long',
  });

  const parts = formatter.formatToParts(value);

  const unitPart = parts.find((part) => part.type === 'unit');

  return unitPart ? unitPart.value.charAt(0).toUpperCase() + unitPart.value.slice(1) : '';
};

const getRussianHumanizeRRule = (rruleInstance: RRule): string => {
  const options = rruleInstance.options;
  const interval = options.interval || 1;
  const freq = options.freq;
  const untilDate = options.until;
  const count = options.count;
  const byweekday = options.byweekday;
  const bysetpos = options.bysetpos;
  const bymonthday = options.bymonthday;
  const bynmonthday = options.bynmonthday;

  if (freq === RRule.DAILY) {
    const untilStr = getUntilDateStr(untilDate);
    const countStr = getReccurencyCountStr(count);
    const end = untilStr || countStr ? `, ${untilStr || countStr}` : '';

    if (interval === 1) {
      return `Каждый день${end}`;
    }

    return `Каждые ${interval} ${getUnitLabel(interval, 'day')}${end}`;
  }

  if (freq === RRule.WEEKLY) {
    const untilStr = getUntilDateStr(untilDate);
    const countStr = getReccurencyCountStr(count);
    const end = untilStr || countStr ? `, ${untilStr || countStr}` : '';

    if (interval === 1) {
      return `Каждую неделю ${getDayNames(byweekday)}${end}`;
    }

    return `Каждые ${interval} ${getUnitLabel(interval, 'week')} ${getDayNames(byweekday)}${end}`;
  }

  if (freq === RRule.MONTHLY) {
    const untilStr = getUntilDateStr(untilDate);
    const countStr = getReccurencyCountStr(count);
    const monthDayNumber = bymonthday?.[0];
    const monthDayLastNumber = bynmonthday?.[0];
    const isFirstMatch = bysetpos?.[0] === 1;
    const isFirstMonday = monthDayNumber === RRule.MO.weekday && isFirstMatch;
    const isLastMonthDay = monthDayLastNumber === -1;
    const end = untilStr || countStr ? `, ${untilStr || countStr}` : '';
    const firtsMondayMonthDayStr = isFirstMonday ? 'первый Понедельник' : '';
    const lastMonthtDayStr = isLastMonthDay ? 'последний день' : '';
    const monthDayNumberStr = monthDayNumber ? `${monthDayNumber}-го числа` : '';
    const monthDayStr = lastMonthtDayStr || firtsMondayMonthDayStr || monthDayNumberStr;

    if (interval === 1) {
      return `Каждый месяц ${monthDayStr}${end}`;
    }

    return `Каждые ${interval} ${getUnitLabel(interval, 'month')} ${monthDayStr}${end}`;
  }

  if (freq === RRule.YEARLY) {
    const untilStr = getUntilDateStr(untilDate);
    const countStr = getReccurencyCountStr(count);
    const end = untilStr || countStr ? `, ${untilStr || countStr}` : '';

    if (interval === 1) {
      return `Каждый год${end}`;
    }

    return `Каждые ${interval} ${getUnitLabel(interval, 'year')}${end}`;
  }

  return '';
};

export const getHumanizeRRule = (value: string): string => {
  try {
    const rrule = RRule.fromString(`RRULE:FREQ=${value}`);

    return isRussianLocale(getLanguage()) ? getRussianHumanizeRRule(rrule) : rrule.toText();
  } catch {
    return '';
  }
};
